// components/RequerimientoModal.jsx
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import PlatesInput from "./PlatesInput";
import axios from "../../../config/axiosConfig";
import { buildPayload } from "../utils/payload";
import { extractList } from "../utils/extract";
import { getCatalogoClientesServicio } from "../services/reqSolicitudes";
import debounce from "lodash.debounce";
import { fieldSx } from "../../egresos/components/formStyles";

// Tipos de requerimiento que se pueden emitir desde esta pantalla
const TIPOS_REQ = [
  { value: "3", titulo: "Adición", detalle: "Instalar equipos nuevos o vincular otros al servicio", Icono: AddCircleOutlineIcon },
  { value: "2", titulo: "Mantenimiento", detalle: "Revisar, reparar o reemplazar equipos instalados", Icono: BuildOutlinedIcon },
  { value: "5", titulo: "Devolución", detalle: "Retirar equipos del cliente, o transferirlos a otro", Icono: KeyboardReturnIcon },
];

// Bloque con título (barra de encabezado + cuerpo), el mismo estilo de las secciones de los demás formularios
const Seccion = ({ titulo, nota, children }) => (
  // flexShrink 0: dentro del diálogo (columna con scroll) las secciones no se comprimen, el diálogo hace scroll
  <Box sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper", overflow: "hidden", flexShrink: 0 }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 1.25, py: 0.4, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", borderLeft: "3px solid", borderLeftColor: "primary.main" }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>{titulo}</Typography>
      {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
    </Box>
    <Box sx={{ p: 1.25, display: "grid", gap: 1 }}>{children}</Box>
  </Box>
);

// Opción con casilla que despliega su campo debajo (cliente tercerizado, transferencia de cliente)
const OpcionExtra = ({ activa, titulo, ayuda, children }) => (
  <Box sx={{ border: 1, borderColor: activa ? "primary.main" : "divider", bgcolor: (t) => (activa ? alpha(t.palette.primary.main, 0.05) : "transparent"), px: 1, py: 0.5 }}>
    {titulo}
    {ayuda && <Typography sx={{ fontSize: 11.5, color: "text.secondary", ml: 3.75, mt: -0.25, mb: 0.25 }}>{ayuda}</Typography>}
    {children}
  </Box>
);

const getPlataformaValue = (source) => {
  const rawValue =
    source?.web ??
    source?.plataforma?.id ??
    source?.plataforma_id ??
    source?.plataforma ??
    source?.id_plataforma ??
    source?.id_web ??
    source?.web_id ??
    source?.cod_plataforma ??
    null;
  const raw = typeof rawValue === "object" ? rawValue?.id : rawValue;

  return raw == null || raw === "" ? "" : String(raw);
};

export default function RequerimientoModal({
  open,
  onClose,
  selected,            // cliente seleccionado
  sucursalDefault = "La Paz",
  plataformas,
  fetchPlataformas,
  tecnicos,
  fetchTecnicos,
  onClienteUpdated,
  onSuccessNavigate,   // (cod) => void
}) {
  const [formData, setFormData] = useState({
    tipo_requerimiento: "",
    tipo_adicion: "EQUIPO_NUEVO",
    contacto: "",
    telefono: "",
    descripcion: "",
  });
  const [placas, setPlacas] = useState([]);
  const [placasOptions, setPlacasOptions] = useState([]);
  const [loadingPlacas, setLoadingPlacas] = useState(false);
  const [placasTotal, setPlacasTotal] = useState(null); // cuántas placas hay en el servicio para la búsqueda actual
  const [fechaHora, setFechaHora] = useState("");
  const [lugar, setLugar] = useState("");
  const [sucursal, setSucursal] = useState(sucursalDefault);
  const [tecnicosSel, setTecnicosSel] = useState([]);
  const [plataformaId, setPlataformaId] = useState("");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  const [errores, setErrores] = useState({}); // campos obligatorios que faltan (se marcan en rojo)
  const didInitRef = useRef(false);
  // --- TERCIALIZADO ---
  const [tercializado, setTercializado] = useState(false);
  const [tercInput, setTercInput] = useState("");
  const [tercLoading, setTercLoading] = useState(false);
  const [tercOptions, setTercOptions] = useState([]);
  const [tercSelected, setTercSelected] = useState(null);
  const [tercError, setTercError] = useState(false);
  const tercInputRef = useRef(null);
  // --- TRANSFERENCIA DE CLIENTE (Devolucion) ---
  const [transferenciaCliente, setTransferenciaCliente] = useState(false);
  const [destinoInput, setDestinoInput] = useState("");
  const [destinoLoading, setDestinoLoading] = useState(false);
  const [destinoOptions, setDestinoOptions] = useState([]);
  const [destinoSelected, setDestinoSelected] = useState(null);
  const [destinoError, setDestinoError] = useState(false);
  const destinoInputRef = useRef(null);
  const esAdicion = formData.tipo_requerimiento === "3";
  const esDevolucion = formData.tipo_requerimiento === "5";
  const esVinculacionAdicional = formData.tipo_adicion === "VINCULACION_ADICIONAL";
  const compactFieldSx = { ...fieldSx, "& .MuiFormHelperText-root": { mt: 0.25, fontSize: "0.72rem" } };
  const compactGridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
    gap: 1,
  };

  const getContactoServicioData = useCallback((contacto) => ({
    contacto: contacto?.nombre_contacto || "",
    telefono: contacto?.telefono || contacto?.celular || "",
  }), []);

  const getContactoClienteData = useCallback((contacto) => {
    const nombreCompleto = [
      contacto?.nombre,
      contacto?.paterno,
      contacto?.materno,
    ].filter(Boolean).join(" ");

    return {
      contacto: nombreCompleto || contacto?.nombre_contacto || "",
      telefono: contacto?.telefono_contacto || contacto?.telefono || contacto?.celular || "",
    };
  }, []);

  const normalizePlacaOptions = useCallback((resp) => {
    const items = Array.isArray(resp?.data?.items)
      ? resp.data.items
      : Array.isArray(resp?.items)
      ? resp.items
      : Array.isArray(resp)
      ? resp
      : [];

    return items
      .map((item) => {
        if (typeof item === "string") return { placa: item.trim().toUpperCase() };
        const placa = String(item?.placa || "").trim().toUpperCase();
        if (!placa) return null;
        return {
          ...item,
          placa,
          clientes: Array.isArray(item?.clientes) ? item.clientes : [],
        };
      })
      .filter(Boolean);
  }, []);

  const fetchPlacasCatalogo = useMemo(() => debounce(async (buscar = "") => {
    if (!selected?.cod_servicio_cliente) return;
    try {
      setLoadingPlacas(true);
      const { data } = await axios.get("/api/req-solicitudes/catalogo-placas-vehiculos", {
        params: {
          cod_servicio_cliente: selected.cod_servicio_cliente,
          buscar: buscar || "",
          // Hasta 200 de una vez: la mayoría de los servicios entra completo; los más grandes se buscan escribiendo
          limit: 200,
        },
      });
      setPlacasOptions(normalizePlacaOptions(data));
      const total = data?.total_disponible ?? data?.data?.total_disponible;
      setPlacasTotal(total == null ? null : Number(total));
    } catch (err) {
      console.warn(err);
      setSnack({ open: true, severity: "warning", message: "No se pudo cargar las placas existentes." });
      setPlacasOptions([]);
      setPlacasTotal(null);
    } finally {
      setLoadingPlacas(false);
    }
  }, 300), [normalizePlacaOptions, selected?.cod_servicio_cliente]);
  
  const fetchTercializados = useMemo(() => debounce(async (texto) => {
      if (!selected?.cod_servicio_cliente) return;
      try {
        setTercLoading(true);
        console.debug("FETCH tercializados →", {
          base: String(selected?.cod_servicio_cliente),
          buscar: texto || "",
          when: new Date().toISOString(),
        });
        const { data } = await axios.get("/api/req-solicitudes/tercializados", {
          params: { base: String(selected.cod_servicio_cliente), buscar: texto || "", limit: 20 },
        });
        console.debug("FETCH tercializados OK, len:", Array.isArray(data) ? data.length : "n/a");
        
        const arr = Array.isArray(data) ? data : [];
        const normalized = arr.map(o => {
          const cod = o.cod_servicio ?? o.cod_servicio_cliente ?? o.id ?? o.codigo ?? "";
          const razon = o.razon_social ?? o.nom ?? o.nombre ?? "";
          return {
            cod_servicio: String(cod),
            razon_social: String(razon),
            descripcion: o.descripcion ?? "",
            tipo: o.tipo ?? "",
            sucursal: o.sucursal ?? o.sucursal_facturar ?? "",
          };
        });
        setTercOptions(normalized);


      } catch (e) {
        console.error(e);
        setSnack({ open: true, severity: "warning", message: "No se pudieron cargar clientes tercerizados." });
        setTercOptions([]);
      } finally {
        setTercLoading(false);
      }
    }, 350), [selected?.cod_servicio_cliente]);

  const fetchClientesDestino = useMemo(() => debounce(async (texto = "") => {
    try {
      setDestinoLoading(true);
      const { data } = await getCatalogoClientesServicio(texto, 1, 30);
      const items = Array.isArray(data?.data?.items)
        ? data.data.items
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];
      const normalized = items
        .map((item) => ({
          cod_servicio: String(item?.cod_servicio_cliente ?? ""),
          cod_cliente: item?.cod_cliente ?? "",
          razon_social: String(item?.razon_social ?? item?.nombre ?? ""),
          descripcion: item?.descripcion ?? "",
          sucursal: item?.sucursal_facturar ?? item?.departamento ?? "",
        }))
        .filter((item) => item.cod_servicio && item.razon_social
          && String(item.cod_servicio) !== String(selected?.cod_servicio_cliente));
      setDestinoOptions(normalized);
    } catch (e) {
      console.error(e);
      setDestinoOptions([]);
    } finally {
      setDestinoLoading(false);
    }
  }, 350), [selected?.cod_servicio_cliente]);

    // cancelar cualquier llamada pendiente al desmontar/cerrar
    useEffect(() => {
      return () => {
        fetchTercializados.cancel?.();
        fetchPlacasCatalogo.cancel?.();
        fetchClientesDestino.cancel?.();
      };
    }, [fetchTercializados, fetchPlacasCatalogo, fetchClientesDestino]);

    useEffect(() => {
      if (!open) return;
      if (tercializado && esAdicion && selected?.cod_servicio_cliente) {
        fetchTercializados("");
      }
    }, [open, tercializado, esAdicion, selected?.cod_servicio_cliente, fetchTercializados]);

    useEffect(() => {
      if (!esVinculacionAdicional || !tercializado) return;
      setTercializado(false);
      setTercError(false);
      setTercSelected(null);
      setTercInput("");
    }, [esVinculacionAdicional, tercializado]);

    useEffect(() => {
      if (!esDevolucion && transferenciaCliente) {
        setTransferenciaCliente(false);
        setDestinoError(false);
        setDestinoSelected(null);
        setDestinoInput("");
      }
    }, [esDevolucion, transferenciaCliente]);

  // dentro de RequerimientoModal
useEffect(() => {
  if (!open) {
    didInitRef.current = false;
    setTercializado(false);
    setTercInput("");
    setTercOptions([]);
    setTercSelected(null);
    setTransferenciaCliente(false);
    setDestinoInput("");
    setDestinoOptions([]);
    setDestinoSelected(null);
    setDestinoError(false);
    return;
  }
  if (didInitRef.current) return; // evita segunda corrida en StrictMode dev
  didInitRef.current = true;

  setErrores({});
  setFormData({
    tipo_requerimiento: "",
    tipo_adicion: "EQUIPO_NUEVO",
    contacto: selected?.contacto_nombre_completo || "",
    telefono: selected?.telefono_contacto || "",
    descripcion: "",
  });
  setPlacas([]);
  setPlacasOptions([]); 
  setFechaHora("");
  setLugar("");
  setSucursal(sucursalDefault);
  setTecnicosSel([]);

  fetchTecnicos(sucursalDefault);
  fetchPlataformas();

  setPlataformaId(getPlataformaValue(selected));
}, [open, selected, sucursalDefault, fetchTecnicos, fetchPlataformas]);

useEffect(() => {
  if (!open || !selected?.cod_servicio_cliente) return;

  let cancel = false;

  (async () => {
    try {
      const { data } = await axios.get(`/api/cliente-servicio/${selected.cod_servicio_cliente}`);
      if (cancel) return;

      const info = data?.data || data || {};
      const plataformaActual = getPlataformaValue(info);
      setPlataformaId(plataformaActual);
      onClienteUpdated?.(selected.cod_servicio_cliente, {
        web: plataformaActual === "" ? null : Number(plataformaActual),
        plataforma: plataformaActual === "" ? null : Number(plataformaActual),
      });
    } catch (err) {
      console.warn("No se pudo cargar la plataforma actual del cliente-servicio.", err);
    }
  })();

  return () => {
    cancel = true;
  };
}, [open, selected?.cod_servicio_cliente, onClienteUpdated]);

useEffect(() => {
  if (!open || !selected?.cod_cliente || !selected?.cod_servicio_cliente) return;

  let cancel = false;
  const codCliente = encodeURIComponent(selected.cod_cliente);
  const codServicioCliente = encodeURIComponent(selected.cod_servicio_cliente);

  (async () => {
    try {
      const servicioResp = await axios.get(
        `/api/clientes/${codCliente}/servicios/${codServicioCliente}/contactos`
      );
      const contactosServicio = extractList(servicioResp?.data);

      if (cancel) return;

      const contactoServicio = contactosServicio.length > 0
        ? getContactoServicioData(contactosServicio[0])
        : null;

      if (contactoServicio?.contacto && contactoServicio?.telefono) {
        setFormData((prev) => ({
          ...prev,
          contacto: contactoServicio.contacto,
          telefono: contactoServicio.telefono,
        }));
        return;
      }

      const clienteResp = await axios.get(`/api/clientes/${codCliente}/contactos`);
      const contactosCliente = extractList(clienteResp?.data);

      if (cancel) return;

      if (contactosCliente.length === 0) {
        if (contactoServicio) {
          setFormData((prev) => ({
            ...prev,
            contacto: contactoServicio.contacto || prev.contacto,
            telefono: contactoServicio.telefono || prev.telefono,
          }));
        }
        return;
      }

      const contactoCliente = getContactoClienteData(contactosCliente[0]);
      setFormData((prev) => ({
        ...prev,
        contacto: contactoServicio?.contacto || contactoCliente.contacto || prev.contacto,
        telefono: contactoServicio?.telefono || contactoCliente.telefono || prev.telefono,
      }));
    } catch (err) {
      console.warn("No se pudieron cargar contactos del cliente.", err);
    }
  })();

  return () => {
    cancel = true;
  };
}, [getContactoClienteData, getContactoServicioData, open, selected?.cod_cliente, selected?.cod_servicio_cliente]);


  // si cambia sucursal, recargar técnicos
  useEffect(() => {
    if (!open) return;
    setTecnicosSel([]);
    fetchTecnicos(sucursal);
  }, [open, sucursal, fetchTecnicos]);

  // Si cambia el tipo, y ya no es Adición, resetea el bloque de tercerizado
  useEffect(() => {
    if (!open) return;
    if (!esAdicion) {
      setTercializado(false);
      setTercSelected(null);
      setTercInput("");
      setTercOptions([]);
      setTercLoading(false);
    }
  }, [open, esAdicion]);

  

   useEffect(() => {
    if (!open) return;
    const tipo = formData.tipo_requerimiento;
    const cod = selected?.cod_servicio_cliente;
    const requierePlacasExistentes = tipo === "2" || tipo === "5";

    if (!requierePlacasExistentes) {
      setPlacasOptions([]); // en Adición u otros, no restringimos
      return;
    }
    if (!cod) return;

    fetchPlacasCatalogo("");


  }, [fetchPlacasCatalogo, open, formData.tipo_requerimiento, selected?.cod_servicio_cliente]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // validaciones mínimas: se revisan todas juntas y se marcan en el formulario
    const faltan = {};
    if (!formData.tipo_requerimiento) faltan.tipo = "Elige el tipo de requerimiento";
    if (!fechaHora) faltan.fecha = "Indica la fecha y la hora";
    if (!formData.descripcion?.trim()) faltan.descripcion = "Escribe qué se debe hacer";
    setErrores(faltan);
    if (Object.keys(faltan).length) {
      setSnack({ open: true, severity: "warning", message: `Falta completar: ${[faltan.tipo && "tipo", faltan.fecha && "fecha y hora", faltan.descripcion && "descripción"].filter(Boolean).join(", ")}.` });
      const primero = faltan.tipo ? "req-tipo" : faltan.fecha ? "req-fecha" : "req-descripcion";
      setTimeout(() => document.getElementById(primero)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }

    // requerido: cliente tercerizado si está marcado en Adición
    if (esAdicion && tercializado && !tercSelected) {
      setTercError(true);
      setSnack({ open: true, severity: "warning", message: "Debes seleccionar un cliente tercerizado." });
      tercInputRef.current?.focus();
      return;
    }

    // requerido: cliente destino si está marcada la transferencia de cliente
    if (esDevolucion && transferenciaCliente && !destinoSelected) {
      setDestinoError(true);
      setSnack({ open: true, severity: "warning", message: "Debes seleccionar un cliente destino." });
      destinoInputRef.current?.focus();
      return;
    }

    try {
      setSaving(true);

      // actualizar plataforma en cliente-servicio (si cambia)
      const plataformaNueva = plataformaId === "" || plataformaId == null ? null : Number(plataformaId);
      const plataformaActualRaw = selected?.web ?? selected?.plataforma;
      const plataformaActual = plataformaActualRaw == null || plataformaActualRaw === "" ? null : Number(plataformaActualRaw);
      
      if (plataformaNueva !== plataformaActual) {
        try {
          await axios.put(`/api/cliente-servicio/${selected.cod_servicio_cliente}`, { web: plataformaNueva });
          onClienteUpdated?.(selected.cod_servicio_cliente, {
            web: plataformaNueva,
            plataforma: plataformaNueva,
          });
        } catch (err) {
          console.warn("No se pudo actualizar la plataforma del cliente-servicio", err);
          // opcional: snack no bloqueante
          setSnack({ open: true, severity: "warning", message: "No se pudo actualizar la plataforma del cliente." });
        }
      }


      // crear requerimiento (sin plataforma)
      const payload = buildPayload({
        tipo_requerimiento: formData.tipo_requerimiento,
        tipo_adicion: esAdicion ? formData.tipo_adicion : "",
        cod_servicio_cliente: selected.cod_servicio_cliente,
        sucursal,
        contacto: formData.contacto,
        telefono: formData.telefono,
        descripcion: formData.descripcion,
        fechaHora,
        lugar,
        placas,
        tecnicos: tecnicosSel,
        tercia: esAdicion && tercializado && tercSelected
          ? String(tercSelected.cod_servicio)
          : 0,
        cod_servicio_cliente_tercializado:
        esAdicion && tercializado && tercSelected ? String(tercSelected.cod_servicio) : null,
        es_transferencia_cliente: esDevolucion && transferenciaCliente && destinoSelected ? 1 : 0,
        cod_servicio_destino: esDevolucion && transferenciaCliente && destinoSelected
          ? destinoSelected.cod_servicio
          : null,
      });

      const resp = await axios.post("/api/req-solicitudes", payload);
      const cod = resp?.data?.requerimiento?.cod ?? resp?.data?.cod ?? resp?.data?.data?.requerimiento?.cod;

      if (cod) {
        setSnack({ open: true, severity: "success", message: "Requerimiento creado." });
        onSuccessNavigate?.(cod);
        return;
      }

      setSnack({ open: true, severity: "warning", message: "Se creó el requerimiento pero no recibí el código." });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: "error", message: "No se pudo crear el requerimiento." });
    } finally {
      setSaving(false);
    }
  };

  const tipoSel = TIPOS_REQ.find((t) => t.value === formData.tipo_requerimiento);
  const soloExistentes = formData.tipo_requerimiento === "2" || formData.tipo_requerimiento === "5";
  const ayudaPlacas = soloExistentes
    ? "Elige entre las placas que el cliente ya tiene registradas."
    : esAdicion ? "Escribe las placas de los vehículos donde se instalará (pueden ser nuevas)." : "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      {/* Encabezado: para quién es el requerimiento */}
      <DialogTitle sx={{ py: 1.25, px: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 800, lineHeight: 1.2 }}>Emitir requerimiento</Typography>
        {selected && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
            <Typography sx={{ fontSize: "0.86rem", fontWeight: 700 }}>{selected.nombre_cliente || selected.razon_social}</Typography>
            <Chip size="small" variant="outlined" label={`#${selected.cod_servicio_cliente}`} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
            {selected.nombre_servicio && <Chip size="small" color="primary" label={selected.nombre_servicio} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />}
            {selected.descripcion && <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>{selected.descripcion}</Typography>}
          </Box>
        )}
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            px: 2,
            py: 1.5,
            bgcolor: "background.default",
            "& .MuiFormControlLabel-root": { my: -0.25 },
            "& .MuiFormControlLabel-label": { fontSize: "0.82rem", fontWeight: 600 },
          }}
        >
          {/* 1. Tipo */}
          <Seccion titulo="Tipo de requerimiento">
            <Box id="req-tipo" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1 }}>
              {TIPOS_REQ.map(({ value, titulo, detalle, Icono }) => {
                const activo = formData.tipo_requerimiento === value;
                return (
                  <Box
                    key={value} role="button" tabIndex={0}
                    onClick={() => { setFormData({ ...formData, tipo_requerimiento: value }); setErrores((e) => ({ ...e, tipo: undefined })); }}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setFormData({ ...formData, tipo_requerimiento: value }), setErrores((x) => ({ ...x, tipo: undefined })))}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1, p: 1, cursor: "pointer", border: 1.5,
                      borderColor: activo ? "primary.main" : errores.tipo ? "error.main" : "divider",
                      bgcolor: (t) => (activo ? alpha(t.palette.primary.main, 0.08) : "background.paper"),
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <Box sx={{ width: 34, height: 34, flex: "0 0 auto", display: "grid", placeItems: "center", borderRadius: "50%", color: activo ? "primary.contrastText" : "primary.main", bgcolor: (t) => (activo ? t.palette.primary.main : alpha(t.palette.primary.main, 0.1)) }}>
                      <Icono fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, lineHeight: 1.2 }}>{titulo}</Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.25 }}>{detalle}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {errores.tipo && <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 700 }}>{errores.tipo}</Typography>}

            {esAdicion && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>Tipo de adición:</Typography>
                <ToggleButtonGroup
                  exclusive size="small" value={formData.tipo_adicion || "EQUIPO_NUEVO"}
                  onChange={(_, v) => v && setFormData({ ...formData, tipo_adicion: v })}
                  sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, py: 0.25, px: 1.5, borderRadius: 0 } }}
                >
                  <ToggleButton value="EQUIPO_NUEVO">Equipo nuevo</ToggleButton>
                  <ToggleButton value="VINCULACION_ADICIONAL">Vinculación adicional</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Seccion>

          {/* 2. Contacto y plataforma */}
          <Seccion titulo="Contacto y plataforma">
            <Box sx={compactGridSx}>
              <TextField size="small" label="Contacto" value={formData.contacto} sx={compactFieldSx}
                onChange={(e) => setFormData({ ...formData, contacto: e.target.value })} />
              <TextField size="small" label="Teléfono" value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} inputMode="tel" sx={compactFieldSx} />
            </Box>
            <FormControl size="small" fullWidth sx={compactFieldSx}>
              <InputLabel id="plataforma-label" shrink>Plataforma</InputLabel>
              <Select
                labelId="plataforma-label"
                label="Plataforma"
                value={plataformaId ?? ""}
                onChange={(e) => setPlataformaId(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === "" || selected == null) {
                    return <span style={{ color: "#94a3b8" }}>Sin plataforma</span>;
                  }
                  const p = plataformas?.find?.(x => String(x.id) === String(selected));
                  return p ? p.nombre : `Plataforma #${selected}`;
                }}
              >
                <MenuItem value=""><em>Sin plataforma</em></MenuItem>
                {plataformas.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)} dense>
                    <ListItemText
                      primary={p.nombre}
                      secondary={p.url_plataforma?.replace(/^https?:\/\//, "") || ""}
                      primaryTypographyProps={{ fontSize: "0.82rem" }}
                      secondaryTypographyProps={{ fontSize: "0.72rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Seccion>

          {/* 3. Vehículos y equipos */}
          <Seccion titulo="Vehículos" nota={ayudaPlacas}>
            <PlatesInput
              value={placas}
              onChange={setPlacas}
              options={placasOptions}
              restrictToOptions={soloExistentes}
              loading={loadingPlacas}
              totalDisponible={soloExistentes ? placasTotal : null}
              onSearchInputChange={soloExistentes ? fetchPlacasCatalogo : undefined}
            />

            {/* Cliente tercerizado (solo Adición) */}
            {esAdicion && !esVinculacionAdicional && (
              <OpcionExtra
                activa={tercializado}
                ayuda="Los equipos se instalan para otro cliente, que es quien usa el servicio."
                titulo={
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tercializado}
                        size="small"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setTercializado(checked);
                          if (!checked) setTercError(false);
                          if (checked) {
                            setTercSelected(null);
                            setTercInput("");
                            fetchTercializados("");
                          }
                        }}
                      />
                    }
                    label="Cliente tercerizado"
                  />
                }
              >
                {tercializado && (
                  <Autocomplete
                    size="small"
                    sx={{ ...compactFieldSx, mt: 0.5, mb: 0.5 }}
                    options={tercOptions}
                    filterOptions={(x) => x}
                    loading={tercLoading}
                    value={tercSelected}
                    onOpen={() => {
                      if (!tercOptions.length && !tercLoading) {
                        fetchTercializados("");
                      }
                    }}
                    onChange={(_, val) => {
                      setTercSelected(val);
                      if (val) setTercError(false);
                    }}
                    inputValue={tercInput}
                    onInputChange={(_, val) => {
                      setTercInput(val);
                      fetchTercializados(val);
                    }}
                    getOptionLabel={(o) => o?.razon_social ? `${o.razon_social}` : ""}
                    isOptionEqualToValue={(o, v) => String(o.cod_servicio) === String(v.cod_servicio)}
                    noOptionsText={tercLoading ? "Cargando..." : "Sin coincidencias"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar cliente tercerizado…"
                        placeholder="Razón social / descripción / sucursal"
                        required
                        error={tercError}
                        helperText={tercError ? "Selecciona un cliente tercerizado." : ""}
                        inputRef={tercInputRef}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {tercLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{ style: { maxHeight: 220, fontSize: "0.82rem" } }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.cod_servicio} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, paddingTop: 4, paddingBottom: 4 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <b style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {option.razon_social}
                          </b>
                          <small style={{ color: "#64748b" }}>
                            {option.descripcion} • {option.tipo} • {option.sucursal}
                          </small>
                        </Box>
                        <Chip size="small" label={`#${option.cod_servicio}`} />
                      </li>
                    )}
                  />
                )}
              </OpcionExtra>
            )}

            {/* Transferencia de cliente (solo Devolución) */}
            {esDevolucion && (
              <OpcionExtra
                activa={transferenciaCliente}
                ayuda="Los equipos pasan directamente a otro cliente en lugar de volver al almacén."
                titulo={
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={transferenciaCliente}
                        size="small"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setTransferenciaCliente(checked);
                          if (!checked) setDestinoError(false);
                          if (checked) {
                            setDestinoSelected(null);
                            setDestinoInput("");
                            fetchClientesDestino("");
                          }
                        }}
                      />
                    }
                    label="Transferencia de cliente"
                  />
                }
              >
                {transferenciaCliente && (
                  <Autocomplete
                    size="small"
                    sx={{ ...compactFieldSx, mt: 0.5, mb: 0.5 }}
                    options={destinoOptions}
                    filterOptions={(x) => x}
                    loading={destinoLoading}
                    value={destinoSelected}
                    onOpen={() => {
                      if (!destinoOptions.length && !destinoLoading) {
                        fetchClientesDestino("");
                      }
                    }}
                    onChange={(_, val) => {
                      setDestinoSelected(val);
                      if (val) setDestinoError(false);
                    }}
                    inputValue={destinoInput}
                    onInputChange={(_, val) => {
                      setDestinoInput(val);
                      fetchClientesDestino(val);
                    }}
                    getOptionLabel={(o) => o?.razon_social ? `${o.razon_social}` : ""}
                    isOptionEqualToValue={(o, v) => String(o.cod_servicio) === String(v.cod_servicio)}
                    noOptionsText={destinoLoading ? "Cargando..." : "Sin coincidencias"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar cliente destino…"
                        placeholder="Razón social / descripción / sucursal"
                        required
                        error={destinoError}
                        helperText={destinoError ? "Selecciona un cliente destino." : "Al confirmar la devolución, los equipos se vincularán automáticamente a este cliente en un requerimiento de Adición nuevo."}
                        inputRef={destinoInputRef}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {destinoLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{ style: { maxHeight: 220, fontSize: "0.82rem" } }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.cod_servicio} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, paddingTop: 4, paddingBottom: 4 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <b style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {option.razon_social}
                          </b>
                          <small style={{ color: "#64748b" }}>
                            {option.descripcion} • {option.sucursal}
                          </small>
                        </Box>
                        <Chip size="small" label={`#${option.cod_servicio}`} />
                      </li>
                    )}
                  />
                )}
              </OpcionExtra>
            )}
          </Seccion>

          {/* 4. Programación */}
          <Seccion titulo="Programación" nota="cuándo, dónde y quién">
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr 0.9fr" }, gap: 1 }}>
              <TextField id="req-fecha" size="small" label="Fecha y hora" type="datetime-local" value={fechaHora}
                onChange={(e) => { setFechaHora(e.target.value); setErrores((x) => ({ ...x, fecha: undefined })); }}
                InputLabelProps={{ shrink: true }} required error={!!errores.fecha} helperText={errores.fecha || ""} sx={compactFieldSx} />
              <TextField size="small" label="Lugar" value={lugar} onChange={(e) => setLugar(e.target.value)} sx={compactFieldSx} />
              <FormControl size="small" fullWidth sx={compactFieldSx}>
                <InputLabel id="sucursal-label">Sucursal</InputLabel>
                <Select labelId="sucursal-label" label="Sucursal" value={sucursal} onChange={(e) => setSucursal(e.target.value)}>
                  <MenuItem value="La Paz">La Paz</MenuItem>
                  <MenuItem value="Cochabamba">Cochabamba</MenuItem>
                  <MenuItem value="Santa Cruz">Santa Cruz</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControl size="small" fullWidth sx={compactFieldSx}>
              <InputLabel id="tecnicos-label">Técnicos</InputLabel>
              <Select
                labelId="tecnicos-label"
                label="Técnicos"
                multiple
                value={tecnicosSel}
                onChange={(e) => setTecnicosSel(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((username) => {
                      const t = tecnicos.find(x => x.username === username);
                      return <Chip key={username} label={t ? t.nombre : username} size="small" color="primary" sx={{ height: 20, fontSize: "0.72rem" }} />;
                    })}
                  </Box>
                )}
              >
                {tecnicos.map((t) => (
                  <MenuItem key={t.username} value={t.username} dense>
                    <Checkbox checked={tecnicosSel.indexOf(t.username) > -1} size="small" />
                    <ListItemText
                      primary={t.nombre}
                      secondary={t.sucursal}
                      primaryTypographyProps={{ fontSize: "0.82rem" }}
                      secondaryTypographyProps={{ fontSize: "0.72rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Seccion>

          {/* 5. Descripción */}
          <Seccion titulo="Descripción" nota="qué se debe hacer">
            <TextField id="req-descripcion" size="small" value={formData.descripcion} placeholder={tipoSel ? `Ej.: detalle del trabajo de ${tipoSel.titulo.toLowerCase()}…` : "Describe el trabajo que se debe realizar…"}
              onChange={(e) => { setFormData({ ...formData, descripcion: e.target.value }); setErrores((x) => ({ ...x, descripcion: undefined })); }}
              multiline minRows={3} fullWidth required error={!!errores.descripcion} helperText={errores.descripcion || ""} sx={compactFieldSx} />
          </Seccion>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider", gap: 1 }}>
          {tipoSel && <Typography sx={{ fontSize: 12, color: "text.secondary", mr: "auto" }}>Se emitirá una <b>{tipoSel.titulo}</b>{placas.length ? ` para ${placas.length} vehículo${placas.length === 1 ? "" : "s"}` : ""}.</Typography>}
          <Button onClick={onClose} disabled={saving} size="small" sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: "none", fontWeight: 700, minWidth: 170 }}>
            {saving ? "Guardando…" : "Emitir requerimiento"}
          </Button>
        </DialogActions>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
